import { computed, inject, isRef } from "vue";
import { storeToRefs } from "pinia";
import { ActionList, useActions } from "@/SharedComponents/BulkActions";
import {
    AssetGroups,
    Categories,
    Date as DateComponent,
    Priorities,
    Tags,
    Users,
    Visibility,
} from "@/SharedComponents/BulkActions/Steps";
import { FilterType as ActionType } from "@/SharedComponents/Filters";
import { useSelectedRows } from "@/Composables/useSelectedRows";
import { Steps } from "@/Composables/useSteps";
import { toDate } from "@/Utils/date";
import { useI18n } from "vue-i18n";

export function useBulkActions(storeKey, buildingIdsRef, selectionRef) {
    const _storeKey = computed(() =>
        isRef(storeKey) ? storeKey.value : storeKey
    );

    const selectedRowsStore = useSelectedRows(_storeKey);
    const { selection } = storeToRefs(selectedRowsStore);

    const countSelected = computed(() => selection.value.length);

    const { handleClearSelection } = selectedRowsStore;

    const singleBuilding = computed(() => buildingIdsRef?.value.length === 1);

    const linkedTags = computed(() => {
        const tags = selectionRef?.value.reduce((acc, tagable) => {
            tagable.tags?.forEach((tag) => {
                if (!acc[tag.id]) {
                    acc[tag.id] = { ...tag, checked: 0 };
                }
                acc[tag.id].checked++;
            });
            return acc;
        }, {});

        const totalTagables = selectionRef?.value.length;

        return Object.values(tags).map((tag) => ({
            ...tag,
            checked: tag.checked === totalTagables ? true : "indeterminate",
        }));
    });

    let linkedUsers, hasLimitedAccess, hasCompleted;
    let minExpirationDate, maxNotificationDate, hasExpiring, hasNonExpiring;
    if (_storeKey.value === "tasks") {
        linkedUsers = computed(() => {
            const users = selectionRef?.value.reduce((acc, task) => {
                task.assignees.forEach((user) => {
                    if (!acc[user.id]) {
                        acc[user.id] = { ...user, checked: 0 };
                    }
                    acc[user.id].checked++;
                });
                return acc;
            }, {});

            const totalTasks = selectionRef?.value.length;

            return Object.values(users).map((user) => ({
                ...user,
                checked: user.checked === totalTasks ? true : "indeterminate",
            }));
        });

        const { hasPermission, permissions } = inject("permissions");

        hasLimitedAccess = computed(() =>
            buildingIdsRef.value.some((buildingId) =>
                hasPermission(permissions.limitedAccess, buildingId)
            )
        );

        hasCompleted = computed(() =>
            selectionRef?.value.some((task) => task.completed_at)
        );
    } else if (_storeKey.value === "files") {
        minExpirationDate = computed(() =>
            toDate(
                selectionRef?.value.reduce(
                    (min, file) =>
                        min
                            ? file.expiration_date < min
                                ? file.expiration_date
                                : min
                            : file.expiration_date,
                    null
                )
            )
        );

        maxNotificationDate = computed(() =>
            toDate(
                selectionRef?.value.reduce(
                    (max, file) =>
                        max
                            ? file.reminder_date > max
                                ? file.reminder_date
                                : max
                            : file.reminder_date,
                    null
                )
            )
        );

        hasExpiring = computed(() =>
            selectionRef?.value.some((file) => file.expiration_date)
        );

        hasNonExpiring = computed(() =>
            selectionRef?.value.some((file) => !file.expiration_date)
        );
    }

    const { t } = useI18n();
    const { actions: allActions, getActionTitle } = useActions();

    const actions = computed(() => {
        switch (_storeKey.value) {
            case "tasks":
                return [
                    {
                        ...allActions[ActionType.DUE_DATE],
                        disabled: hasCompleted?.value,
                    },
                    {
                        ...allActions[ActionType.PRIORITY],
                        disabled:
                            hasLimitedAccess?.value || hasCompleted?.value,
                    },
                    {
                        ...allActions[ActionType.CATEGORY],
                        disabled:
                            hasLimitedAccess?.value || hasCompleted?.value,
                    },
                    {
                        ...allActions[ActionType.ASSIGNED_TO],
                        disabled:
                            !singleBuilding.value ||
                            hasLimitedAccess?.value ||
                            hasCompleted?.value,
                    },
                    {
                        ...allActions[ActionType.TAGS],
                        disabled:
                            !singleBuilding.value || hasLimitedAccess?.value,
                    },
                    {
                        ...allActions[ActionType.VISIBILITY],
                        disabled:
                            hasLimitedAccess?.value || hasCompleted?.value,
                    },
                ];
            case "files":
                return [
                    allActions[ActionType.EXPIRATION_DATE],
                    {
                        ...allActions[ActionType.NOTIFICATION_DATE],
                        disabled: hasNonExpiring?.value,
                    },
                    {
                        ...allActions[ActionType.TAGS],
                        disabled: !singleBuilding.value,
                    },
                    allActions[ActionType.VISIBILITY],
                ];
            case "assets":
                return [
                    allActions[ActionType.CATEGORY],
                    {
                        ...allActions[ActionType.ASSET_GROUPS],
                        disabled: !singleBuilding.value,
                    },
                    {
                        ...allActions[ActionType.TAGS],
                        disabled: !singleBuilding.value,
                    },
                ];
            default:
                return [];
        }
    });

    const hasActions = computed(() =>
        actions.value.some((action) => !action.disabled)
    );

    const allSteps = computed(() => ({
        [ActionType.DUE_DATE]: {
            title: getActionTitle(ActionType.DUE_DATE),
            content: DateComponent,
            props: {
                minDate: new Date(),
            },
        },
        [ActionType.EXPIRATION_DATE]: {
            title: getActionTitle(ActionType.EXPIRATION_DATE),
            showClear: hasExpiring?.value,
            content: DateComponent,
            props: {
                minDate: maxNotificationDate?.value,
            },
        },
        [ActionType.NOTIFICATION_DATE]: {
            title: getActionTitle(ActionType.NOTIFICATION_DATE),
            content: DateComponent,
            props: {
                minDate: new Date(),
                maxDate: minExpirationDate?.value,
            },
        },
        [ActionType.PRIORITY]: {
            title: getActionTitle(ActionType.PRIORITY),
            content: Priorities,
        },
        [ActionType.CATEGORY]: {
            title: getActionTitle(ActionType.CATEGORY),
            withSearch: true,
            content: Categories,
            props: {
                class: "h-88",
            },
        },
        [ActionType.ASSIGNED_TO]: {
            title: getActionTitle(ActionType.ASSIGNED_TO),
            withSearch: true,
            showClear: linkedUsers?.value.length > 0,
            content: Users,
            props: {
                initialState: linkedUsers?.value,
                class: "min-h-64",
            },
        },
        [ActionType.TAGS]: {
            title: getActionTitle(ActionType.TAGS),
            withSearch: true,
            showClear: linkedTags?.value.length > 0,
            content: Tags,
            props: {
                initialState: linkedTags?.value,
                disableCreate: true,
                class: "min-h-64",
            },
        },
        [ActionType.VISIBILITY]: {
            title: getActionTitle(ActionType.VISIBILITY),
            content: Visibility,
        },
        [ActionType.ASSET_GROUPS]: {
            title: getActionTitle(ActionType.ASSET_GROUPS),
            withSearch: true,
            content: AssetGroups,
            props: {
                buildingId: buildingIdsRef?.value[0],
                showActionButton: false,
                class: "h-88 p-0",
            },
        },
    }));

    const steps = computed(
        () =>
            new Steps({
                list: {
                    title: t("components.bulk_actions.title"),
                    description: t("components.bulk_actions.description"),
                    content: ActionList,
                    props: {
                        actions: actions.value,
                    },
                },
                ...Object.fromEntries(
                    new Map(
                        actions.value.map((action) => [
                            action.id,
                            allSteps.value[action.id],
                        ])
                    )
                ),
            })
    );

    const hints = computed(() => {
        let hints = [];
        if (_storeKey.value === "tasks" && hasLimitedAccess?.value) {
            hints.push(
                t("components.bulk_actions.notice.hints.limited_access")
            );
        } else if (!singleBuilding?.value) {
            hints.push(
                t(
                    `components.bulk_actions.notice.hints.cross_building.${_storeKey.value}`
                )
            );
        }
        if (_storeKey.value === "tasks" && hasCompleted?.value) {
            hints.push(
                t("components.bulk_actions.notice.hints.completed_task")
            );
        }
        if (_storeKey.value === "files" && hasNonExpiring?.value) {
            hints.push(
                t("components.bulk_actions.notice.hints.non_expiring_file")
            );
        }
        return hints;
    });

    return {
        countSelected,
        handleClearSelection,
        hasActions,
        steps,
        hints,
    };
}
